import { useToast } from "@bandit-network/react-ui-kit";
import { useConnect } from "@bandit-network/react-wallet-kit";
import { User } from "@bandit-network/sdk-api";
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQuestContext } from "./QuestContext";
import { QuestSDK } from "@bandit-network/quest-sdk";
import { getTarget } from "../utils";

export interface UserContextType {
  user: User;
  fetchUser: () => Promise<void>;
  onXConnect: (event: any) => void;
  onXDisconnect: (event: any) => void;
  onDiscordConnect: (event: any) => void;
  onDiscordDisconnect: (event: any) => void;
  isUserLoading: boolean;
  isDisconnecting: boolean;
  showRefresh: boolean;
}

export const UserContext = createContext<UserContextType | undefined>(
  undefined,
);

export const UserProvider = ({ children }) => {
  const { address, signature } = useConnect();
  const { questSDK, nonce, nonceExpireBy } = useQuestContext();
  const [user, setUser] = useState<User>();
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showRefresh, setShowRefresh] = useState(false);
  const { toast } = useToast();

  const fetchUser = useCallback(async () => {
    try {
      if (address && questSDK) {
        const userProfile = await questSDK.getUserProfile(address);
        setUser(userProfile);
      }
    } catch (error) {
      console.log(error);
    }
  }, [address, questSDK]);

  const onXConnect = useCallback(
    async (event: any) => {
      try {
        event.stopPropagation();
        setIsLoading(true);
        const _signature = signature?.data
          ? signature?.data
          : await signature?.signMessage(nonce, nonceExpireBy);
        const res = await questSDK.getTwitterAuthUrl({
          walletAddress: address,
          signature: _signature,
        });
        setTimeout(() => {
          window.open(res, getTarget(), "height=600,width=600");
        }, 100);
        const intervalId = setInterval(() => {
          fetchUser();
        }, 1000);

        setTimeout(() => {
          setShowRefresh(true);
          clearInterval(intervalId);
          setIsLoading(false);
        }, 10000);
      } catch (error) {
        toast({
          title: "Connect failed",
          variant: "destructive",
          description: "Something went wrong",
        });
        setIsLoading(false);
      } finally {
      }
    },
    [nonce, nonceExpireBy, signature],
  );

  const onDiscordConnect = useCallback(
    async (event: any) => {
      try {
        event.stopPropagation();
        setIsLoading(true);
        const _signature = signature?.data
          ? signature?.data
          : await signature?.signMessage(nonce, nonceExpireBy);
        const res = await questSDK.getDiscordAuthUrl({
          walletAddress: address,
          signature: _signature,
        });
        setTimeout(() => {
          window.open(res, getTarget(), "height=600,width=600");
        }, 100);
        const intervalId = setInterval(() => {
          fetchUser();
        }, 1000);

        setTimeout(() => {
          setShowRefresh(true);
          clearInterval(intervalId);
          setIsLoading(false);
        }, 10000);
      } catch (error) {
        toast({
          title: "Connect failed",
          variant: "destructive",
          description: "Something went wrong",
        });
        setIsLoading(false);
      } finally {
      }
    },
    [nonce, nonceExpireBy, signature],
  );

  const onXDisconnect = useCallback(
    async (event: any) => {
      try {
        event.stopPropagation();
        setIsDisconnecting(true);
        const _signature = signature?.data
          ? signature?.data
          : await signature?.signMessage(nonce, nonceExpireBy);
        await questSDK.disconnectTwitterProfile(address, _signature);
        await fetchUser();
        toast({
          title: "Disconnected",
          variant: "default",
          description: "Disconnect Successful",
        });
      } catch (error) {
        console.log(error);
        toast({
          title: "Disconnect failed",
          variant: "destructive",
          description: "Something went wrong",
        });
        setIsLoading(false);
      } finally {
        setIsDisconnecting(false);
      }
    },
    [nonce, nonceExpireBy, signature],
  );

  const onDiscordDisconnect = useCallback(
    async (event: any) => {
      try {
        event.stopPropagation();
        setIsDisconnecting(true);
        const _signature = signature?.data
          ? signature?.data
          : await signature?.signMessage(nonce, nonceExpireBy);
        await questSDK.disconnectDiscordProfile(address, _signature);
        await fetchUser();
        toast({
          title: "Disconnected",
          variant: "default",
          description: "Disconnect Successful",
        });
      } catch (error) {
        toast({
          title: "Disconnect failed",
          variant: "destructive",
          description: "Something went wrong",
        });
        setIsLoading(false);
      } finally {
        setIsLoading(false);
      }
    },
    [nonce, nonceExpireBy, signature],
  );

  const value = useMemo(() => {
    return {
      user,
      fetchUser,
      onXConnect,
      onXDisconnect,
      onDiscordConnect,
      onDiscordDisconnect,
      isUserLoading: isLoading,
      isDisconnecting,
      showRefresh,
    };
  }, [
    user,
    fetchUser,
    onXConnect,
    onXDisconnect,
    onDiscordConnect,
    onDiscordDisconnect,
    isLoading,
    isDisconnecting,
    showRefresh,
  ]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
